/**
 * MapProvider — Provider-agnostic map abstraction layer.
 *
 * This wraps the map rendering library (currently Leaflet/OpenStreetMap).
 * All map-specific logic lives here. The rest of the app interacts only
 * through this abstraction so switching providers is a config change.
 *
 * Exports:
 * - MapContainer: The map viewport
 * - MapMarker: A pin on the map
 * - MapCluster: A cluster of pins
 * - useMapViewport: Hook for viewport tracking
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Inject pin animations (once)
if (typeof document !== 'undefined' && !document.getElementById('lk-pin-styles')) {
  const style = document.createElement('style');
  style.id = 'lk-pin-styles';
  style.textContent = `
    @keyframes lk-pin-in {
      from { transform: scale(0.4); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
    @keyframes lk-breathe {
      0%, 100% { transform: scale(1); opacity: inherit; }
      50% { transform: scale(1.08); opacity: 0.3; }
    }
    .lk-map-pin { transition: transform 0.15s ease; cursor: pointer; }
    .lk-map-pin:hover > div > div:first-child { transform: scale(1.1); }
    .lk-map-pin:active > div > div:first-child { transform: scale(0.95); }
    .lk-map-cluster { cursor: pointer; }
    @keyframes lk-card-rise {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

// Fix Leaflet default icon paths (common issue with bundlers)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ============ PIN ICON FACTORY — ROUND BEACON STYLE ============

const PIN_COLORS = {
  activity: '#7A8F5B',   // green
  group: '#E8A87C',      // bloom
  commons: '#7FA5B8',    // sky
  institution: '#7B2D3B', // maroon
  business: '#3B6FA0',   // blue
  sponsor: '#C9A227',    // gold
};

// Energy scale: participation count drives subtle size increase ONLY (no count display)
function getEnergyScale(participantCount) {
  if (participantCount >= 10) return { scale: 1.15, ring: 0.25 };
  if (participantCount >= 6) return { scale: 1.10, ring: 0.18 };
  if (participantCount >= 3) return { scale: 1.05, ring: 0.12 };
  return { scale: 1.0, ring: 0 };
}

function createPinIcon(type, isHighlighted = false, participantCount = 0) {
  const color = PIN_COLORS[type] || '#7A8F5B';
  const energy = getEnergyScale(participantCount);
  const baseSize = 18;
  const size = Math.round(baseSize * energy.scale);
  const totalSize = size + 12;
  const hasRing = energy.ring > 0;
  const hasBreathing = participantCount >= 6 && type === 'activity';

  // NO count inside pin — clean circle only
  const ringHtml = hasRing
    ? `<div style="
        position:absolute;inset:-4px;
        border-radius:50%;
        border:2px solid ${color};
        opacity:${energy.ring};
        ${hasBreathing ? 'animation:lk-breathe 4s ease-in-out infinite;' : ''}
      "></div>`
    : '';

  return L.divIcon({
    className: 'lk-map-pin',
    html: `<div style="
      position:relative;
      width:${totalSize}px;height:${totalSize}px;
      display:flex;align-items:center;justify-content:center;
      ${isHighlighted ? 'transform:scale(1.2);filter:drop-shadow(0 0 4px ' + color + '40);' : ''}
      transition:transform 0.2s ease, filter 0.2s ease;
    ">
      ${ringHtml}
      <div style="
        width:${size}px;height:${size}px;
        background:${color};
        border:2.5px solid white;
        border-radius:50%;
        box-shadow:0 2px 6px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05);
        animation:lk-pin-in 0.3s ease-out;
      "></div>
      <div style="
        position:absolute;bottom:-1px;left:50%;transform:translateX(-50%);
        width:${Math.round(size * 0.6)}px;height:3px;
        background:rgba(0,0,0,0.1);
        border-radius:50%;
        filter:blur(1px);
      "></div>
    </div>`,
    iconSize: [totalSize, totalSize],
    iconAnchor: [totalSize / 2, totalSize / 2],
  });
}

function createClusterIcon(count) {
  // Cluster = multiple activities, NOT participants
  // Neutral style — no energy scaling, no rings
  const size = count > 50 ? 48 : count > 20 ? 42 : 36;
  return L.divIcon({
    className: 'lk-map-cluster',
    html: `<div style="
      width:${size}px;height:${size}px;
      background:#6B7280;
      color:white;
      border:3px solid white;
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font-size:12px;font-weight:700;
      box-shadow:0 2px 6px rgba(0,0,0,0.18);
      animation:lk-pin-in 0.3s ease-out;
    ">${count > 99 ? '99+' : count}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}


// ============ MAP CONTAINER ============

export function MapContainer({
  center,
  zoom = 10,
  pins = [],
  anchors = [],
  highlightedPinId = null,
  onPinClick,
  onAnchorClick,
  onMapClick,
  onViewportChange,
  onMoveEnd,
  style,
  className,
  children,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const anchorMarkersRef = useRef([]);
  const [currentZoom, setCurrentZoom] = useState(zoom);
  const clusterGroupRef = useRef(null);

  // Initialize map
  useEffect(() => {
    if (mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: center || [42.0, -72.7],
      zoom,
      zoomControl: false,
      attributionControl: false,
    });

    // Tile layer — OpenStreetMap (temporary, behind abstraction)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map);

    // Zoom control — bottom right
    L.control.zoom({ position: 'bottomright' }).addTo(map);

    // Attribution — minimal
    L.control.attribution({ position: 'bottomleft', prefix: false })
      .addAttribution('Map data')
      .addTo(map);

    // Map click handler — only dismiss preview, do NOT recenter
    map.on('click', () => {
      if (onMapClick) onMapClick();
    });

    // Disable aggressive map centering behaviors
    map.options.scrollWheelZoom = true;
    map.options.touchZoom = true;

    // Move end handler
    map.on('moveend', () => {
      const bounds = map.getBounds();
      const center = map.getCenter();
      const viewport = {
        center: { lat: center.lat, lng: center.lng },
        zoom: map.getZoom(),
        bounds: {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        },
      };
      if (onViewportChange) onViewportChange(viewport);
      if (onMoveEnd) onMoveEnd(viewport);
    });

    // Zoom change → re-render pins for cluster/uncluster
    map.on('zoomend', () => {
      setCurrentZoom(map.getZoom());
    });

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // Update center when it changes externally
  useEffect(() => {
    if (!mapInstanceRef.current || !center) return;
    const map = mapInstanceRef.current;
    const currentCenter = map.getCenter();
    const dist = Math.abs(currentCenter.lat - center[0]) + Math.abs(currentCenter.lng - center[1]);
    if (dist > 0.01) {
      map.setView(center, zoom, { animate: true });
    }
  }, [center?.[0], center?.[1]]);

  // Render pins with simple clustering
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear existing markers
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    if (!pins.length) return;

    // Simple grid-based clustering
    const currentZoom = map.getZoom();
    const shouldCluster = currentZoom < 12;

    if (shouldCluster) {
      const clusters = clusterPins(pins, currentZoom);
      clusters.forEach(cluster => {
        if (cluster.pins.length === 1) {
          const pin = cluster.pins[0];
          const pCount = pin.preview?.participant_count || 0;
          const marker = L.marker([pin.lat, pin.lng], {
            icon: createPinIcon(pin.type, pin.id === highlightedPinId, pCount),
          });
          marker.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            L.DomEvent.preventDefault(e);
            if (onPinClick) onPinClick(pin);
          });
          marker.addTo(map);
          markersRef.current.push(marker);
        } else {
          const marker = L.marker([cluster.lat, cluster.lng], {
            icon: createClusterIcon(cluster.pins.length),
          });
          marker.on('click', (e) => {
            L.DomEvent.stopPropagation(e);
            L.DomEvent.preventDefault(e);
            // Zoom into cluster — NOT both zoom and recenter simultaneously
            const bounds = L.latLngBounds(cluster.pins.map(p => [p.lat, p.lng]));
            map.fitBounds(bounds, { padding: [60, 60], maxZoom: 14, animate: true, duration: 0.3 });
          });
          marker.addTo(map);
          markersRef.current.push(marker);
        }
      });
    } else {
      pins.forEach(pin => {
        const pCount = pin.preview?.participant_count || 0;
        const marker = L.marker([pin.lat, pin.lng], {
          icon: createPinIcon(pin.type, pin.id === highlightedPinId, pCount),
        });
        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          if (onPinClick) onPinClick(pin);
        });
        marker.addTo(map);
        markersRef.current.push(marker);
      });
    }
  }, [pins, highlightedPinId, currentZoom]);

  // Render anchor locations (faint, tappable for Place View)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    anchorMarkersRef.current.forEach(m => m.remove());
    anchorMarkersRef.current = [];

    anchors.forEach(anchor => {
      const ANCHOR_ICONS = { park: '#6B8F5B', court: '#8B7355', trail: '#5B8F7A', field: '#7B8F5B', venue: '#6B7280' };
      const color = ANCHOR_ICONS[anchor.type] || '#9CA3AF';
      const icon = L.divIcon({
        className: 'lk-anchor-pin',
        html: `<div style="
          width:10px;height:10px;
          background:${color};
          border:1.5px solid white;
          border-radius:50%;
          opacity:0.4;
          box-shadow:0 1px 3px rgba(0,0,0,0.12);
          cursor:pointer;
          transition:opacity 0.15s ease, transform 0.15s ease;
        "></div>`,
        iconSize: [13, 13],
        iconAnchor: [6.5, 6.5],
      });
      const marker = L.marker([anchor.lat, anchor.lng], { icon, interactive: true });
      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        L.DomEvent.preventDefault(e);
        if (onAnchorClick) onAnchorClick(anchor);
      });
      marker.addTo(map);
      anchorMarkersRef.current.push(marker);
    });
  }, [anchors, onAnchorClick]);

  return (
    <div ref={mapRef} style={style} className={className} data-testid="map-container" />
  );
}


// ============ SIMPLE GRID CLUSTERING ============

function clusterPins(pins, zoom) {
  // Tighter grid at closer zooms — ensures pins separate when zooming in
  const gridSize = zoom < 6 ? 5 : zoom < 8 ? 2 : zoom < 10 ? 0.5 : 0.1;
  const grid = {};

  pins.forEach(pin => {
    const key = `${Math.round(pin.lat / gridSize) * gridSize}_${Math.round(pin.lng / gridSize) * gridSize}`;
    if (!grid[key]) {
      grid[key] = { pins: [], latSum: 0, lngSum: 0 };
    }
    grid[key].pins.push(pin);
    grid[key].latSum += pin.lat;
    grid[key].lngSum += pin.lng;
  });

  return Object.values(grid).map(cluster => ({
    lat: cluster.latSum / cluster.pins.length,
    lng: cluster.lngSum / cluster.pins.length,
    pins: cluster.pins,
  }));
}


// ============ FLY TO HELPER ============

export function useMapFlyTo(mapRef) {
  return useCallback((lat, lng, zoom = 13) => {
    if (mapRef.current) {
      mapRef.current.setView([lat, lng], zoom, { animate: true });
    }
  }, []);
}

export default MapContainer;
